/**
 * PracticalSheetEditorPage — Phase C édito (2026-09-21)
 *
 * Éditeur admin d'une fiche pratique. Champs OPTIONNELS. Sélection photo
 * principale + photos d'illustration depuis la médiathèque existante (endpoint
 * /api/photos). Bouton « Générer / préparer avec Blue » → appelle
 * /admin/practical-sheets/{id}/prepare-with-blue puis affiche un panneau
 * SUGGESTIONS que l'admin peut appliquer champ par champ (jamais d'invention).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  getPracticalSheetAdmin,
  updatePracticalSheet,
  prepareSheetWithBlue,
  applySheetBlueDraft,
  getPracticalSheetPdfUrl,
} from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  BookMarked, Sparkles, Save, Check, X, FileDown, Loader2, Image as ImageIcon, ArrowLeft,
} from "lucide-react";
import TranslationsPanel from "../components/admin/TranslationsPanel";
import LivingDossierPanel from "../components/admin/LivingDossierPanel";
import PartnersPanel from "../components/admin/PartnersPanel";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TEXT_FIELDS = [
  ["title", "Titre"],
  ["location_name", "Nom du lieu"],
  ["short_intro", "Courte présentation"],
  ["address", "Adresse"],
  ["how_to_get_there", "Comment s'y rendre"],
  ["opening_hours", "Horaires"],
  ["pricing", "Tarifs"],
  ["suggested_duration", "Durée conseillée"],
  ["accessibility", "Accessibilité"],
  ["luggage_info", "Bagages / consignes"],
  ["must_see", "Incontournables"],
  ["practical_tips", "Conseils pratiques"],
  ["useful_info", "Informations utiles"],
];

const MULTILINE_FIELDS = new Set([
  "short_intro", "how_to_get_there", "must_see", "practical_tips", "useful_info", "address",
]);

function MediaGrid({ photos, selectedCover, selectedIds, onToggleCover, onToggle }) {
  return (
    <div className="grid grid-cols-3 md:grid-cols-4 gap-3 max-h-[420px] overflow-auto p-1">
      {photos.map((p) => {
        const url = p.cloudinary_url || p.public_cloudinary_url;
        const isSelected = selectedIds.has(p.id);
        const isCover = selectedCover === p.id;
        return (
          <div
            key={p.id}
            className={`relative rounded-lg overflow-hidden border-2 transition ${
              isCover ? "border-primary ring-2 ring-primary/40" : isSelected ? "border-emerald-500" : "border-transparent"
            }`}
            data-testid={`media-picker-item-${p.id}`}
          >
            {url ? (
              <img src={url} alt="" className="w-full h-24 object-cover" loading="lazy" />
            ) : (
              <div className="w-full h-24 bg-muted" />
            )}
            <div className="absolute inset-x-0 bottom-0 bg-black/55 text-white text-[10px] px-1.5 py-1 flex justify-between">
              <button
                onClick={() => onToggleCover(p.id)}
                className="hover:underline"
                data-testid={`media-picker-cover-${p.id}`}
              >
                {isCover ? "★ couverture" : "☆ couverture"}
              </button>
              <button
                onClick={() => onToggle(p.id)}
                className="hover:underline"
                data-testid={`media-picker-toggle-${p.id}`}
              >
                {isSelected ? "✓ sélectionné" : "+ ajouter"}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function PracticalSheetEditorPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [openPicker, setOpenPicker] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [bluePanel, setBluePanel] = useState(null); // { updates, missing_fields, note }
  const [applyingKeys, setApplyingKeys] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getPracticalSheetAdmin(id);
      setSheet(r.data);
    } catch (e) {
      toast.error("Fiche introuvable : " + (e?.response?.data?.detail || e?.message || ""));
      nav("/dashboard/fiches-pratiques");
    } finally {
      setLoading(false);
    }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  const loadPhotos = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/photos`, { params: { limit: 120 } });
      setPhotos(r.data?.photos || r.data || []);
    } catch (e) {
      toast.error("Impossible de charger la médiathèque.");
    }
  }, []);

  const openMediaPicker = async () => {
    await loadPhotos();
    setOpenPicker(true);
  };

  const setField = (key, value) => setSheet((s) => ({ ...s, [key]: value }));

  const selectedIds = useMemo(
    () => new Set(sheet?.photo_ids || []),
    [sheet]
  );

  const toggleCover = (pid) => {
    setSheet((s) => ({ ...s, cover_media_id: s.cover_media_id === pid ? null : pid }));
  };
  const togglePhoto = (pid) => {
    setSheet((s) => {
      const arr = new Set(s.photo_ids || []);
      if (arr.has(pid)) arr.delete(pid);
      else arr.add(pid);
      return { ...s, photo_ids: Array.from(arr) };
    });
  };

  const save = async (nextStatus) => {
    if (!sheet) return;
    setSaving(true);
    try {
      const payload = { ...sheet };
      delete payload._cover_url;
      delete payload._photos;
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.created_by;
      if (nextStatus) payload.status = nextStatus;
      const r = await updatePracticalSheet(id, payload);
      setSheet((prev) => ({ ...prev, ...r.data }));
      toast.success(nextStatus === "validated" ? "Fiche validée." : "Fiche enregistrée.");
      await load();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e?.message || "unknown"));
    } finally {
      setSaving(false);
    }
  };

  const runBlue = async () => {
    setPreparing(true);
    setBluePanel(null);
    try {
      const r = await prepareSheetWithBlue(id);
      setBluePanel(r.data);
      const n = Object.keys(r.data?.updates || {}).length;
      toast.success(`Blue a préparé ${n} suggestion(s).`);
    } catch (e) {
      toast.error("Erreur Blue : " + (e?.response?.data?.detail || e?.message || "unknown"));
    } finally {
      setPreparing(false);
    }
  };

  const applyOne = async (key) => {
    if (!bluePanel?.updates || !(key in bluePanel.updates)) return;
    setApplyingKeys((prev) => new Set(prev).add(key));
    try {
      const r = await applySheetBlueDraft(id, { [key]: bluePanel.updates[key] });
      if ((r.data?.applied || []).length > 0) {
        setSheet((prev) => ({ ...prev, ...r.data.sheet }));
        setBluePanel((p) => {
          if (!p) return p;
          const nu = { ...p.updates };
          delete nu[key];
          return { ...p, updates: nu };
        });
        toast.success(`Champ « ${key} » appliqué.`);
      } else {
        toast.error("Champ déjà rempli — Blue n'écrase jamais un contenu humain.");
      }
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e?.message || "unknown"));
    } finally {
      setApplyingKeys((prev) => {
        const n = new Set(prev);
        n.delete(key);
        return n;
      });
    }
  };

  if (loading || !sheet) {
    return (
      <div className="p-12 flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…
      </div>
    );
  }

  return (
    <div data-testid="practical-sheet-editor-page" className="max-w-4xl">
      <header className="mb-6">
        <Button variant="ghost" size="sm" onClick={() => nav("/dashboard/fiches-pratiques")} className="mb-3">
          <ArrowLeft className="h-4 w-4 mr-1" /> Fiches pratiques
        </Button>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-heading text-2xl text-primary flex items-center gap-3">
              <BookMarked className="h-6 w-6" />
              {sheet.title || "Fiche pratique"}
            </h1>
            <p className="text-xs text-muted-foreground mt-1">
              Statut :{" "}
              <span className={`inline-flex text-xs px-2 py-0.5 rounded-full ml-1 ${sheet.status === "validated" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                {sheet.status === "validated" ? "Validée" : "Brouillon"}
              </span>
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={runBlue} disabled={preparing} data-testid="prepare-with-blue-btn">
              {preparing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              Préparer avec Blue
            </Button>
            <Button variant="outline" onClick={openMediaPicker} data-testid="open-media-picker-btn">
              <ImageIcon className="h-4 w-4 mr-2" />
              Photos
            </Button>
            <Button onClick={() => save()} disabled={saving} data-testid="save-sheet-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Enregistrer
            </Button>
            <Button
              variant="default"
              onClick={() => save("validated")}
              disabled={saving || sheet.status === "validated"}
              data-testid="validate-sheet-btn"
            >
              <Check className="h-4 w-4 mr-2" /> Valider
            </Button>
            {sheet.status === "validated" && sheet.slug && (
              <Button variant="outline" asChild data-testid="download-sheet-pdf-btn">
                <a href={getPracticalSheetPdfUrl(sheet.slug)} target="_blank" rel="noreferrer">
                  <FileDown className="h-4 w-4 mr-2" /> PDF membre
                </a>
              </Button>
            )}
          </div>
        </div>
      </header>

      {bluePanel && (
        <section className="mb-6 rounded-lg border border-primary/40 bg-primary/5 p-4" data-testid="blue-suggestions-panel">
          <div className="flex items-start justify-between gap-3 mb-2">
            <h2 className="font-heading text-base text-primary flex items-center gap-2">
              <Sparkles className="h-4 w-4" /> Suggestions de Blue (validation humaine)
            </h2>
            <Button variant="ghost" size="sm" onClick={() => setBluePanel(null)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mb-3">{bluePanel.note}</p>
          {Object.keys(bluePanel.updates || {}).length === 0 ? (
            <div className="text-sm text-muted-foreground">
              Aucune suggestion disponible depuis les sources ({(bluePanel.missing_fields || []).length} champ(s) à compléter manuellement).
            </div>
          ) : (
            <ul className="divide-y divide-primary/10">
              {Object.entries(bluePanel.updates).map(([key, val]) => (
                <li key={key} className="py-2 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">{key}</div>
                    <div className="text-sm mt-0.5 truncate">{typeof val === "string" ? val : JSON.stringify(val)}</div>
                  </div>
                  <Button size="sm" onClick={() => applyOne(key)} disabled={applyingKeys.has(key)} data-testid={`blue-apply-${key}`}>
                    {applyingKeys.has(key) ? <Loader2 className="h-4 w-4 animate-spin" /> : "Appliquer"}
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {(bluePanel.missing_fields || []).length > 0 && (
            <div className="text-xs text-muted-foreground mt-3">
              À compléter manuellement : {bluePanel.missing_fields.join(", ")}
            </div>
          )}
        </section>
      )}

      <Tabs defaultValue="content">
        <TabsList data-testid="editor-tabs">
          <TabsTrigger value="content">Contenu</TabsTrigger>
          <TabsTrigger value="link">Liens éditoriaux</TabsTrigger>
          <TabsTrigger value="meta">Vérification</TabsTrigger>
          <TabsTrigger value="partners" data-testid="tab-partners">Partenaires</TabsTrigger>
        </TabsList>

        <TabsContent value="content" className="mt-4 space-y-4">
          {sheet._cover_url && (
            <div className="rounded-lg overflow-hidden border border-border">
              <img src={sheet._cover_url} alt="cover" className="w-full max-h-[220px] object-cover" />
            </div>
          )}
          {TEXT_FIELDS.map(([key, label]) => (
            <div key={key}>
              <label className="text-sm font-medium">{label}</label>
              {MULTILINE_FIELDS.has(key) ? (
                <Textarea
                  value={sheet[key] || ""}
                  onChange={(e) => setField(key, e.target.value)}
                  data-testid={`sheet-field-${key}`}
                  placeholder="Facultatif"
                />
              ) : (
                <Input
                  value={sheet[key] || ""}
                  onChange={(e) => setField(key, e.target.value)}
                  data-testid={`sheet-field-${key}`}
                  placeholder="Facultatif"
                />
              )}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="link" className="mt-4 space-y-4">
          <div>
            <label className="text-sm font-medium">Voyage (trip_id)</label>
            <Input
              value={sheet.trip_id || ""}
              onChange={(e) => setField("trip_id", e.target.value || null)}
              data-testid="sheet-field-trip_id"
              placeholder="Facultatif — id d'un voyage existant"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Étape (trip_day_id)</label>
            <Input
              value={sheet.trip_day_id || ""}
              onChange={(e) => setField("trip_day_id", e.target.value || null)}
              data-testid="sheet-field-trip_day_id"
              placeholder="Facultatif — id d'une étape existante"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Article (article_id)</label>
            <Input
              value={sheet.article_id || ""}
              onChange={(e) => setField("article_id", e.target.value || null)}
              data-testid="sheet-field-article_id"
              placeholder="Facultatif — id d'un article existant"
            />
          </div>
        </TabsContent>

        <TabsContent value="meta" className="mt-4 space-y-4">
          <div>
            <label className="text-sm font-medium">Date de dernière vérification</label>
            <Input
              type="date"
              value={sheet.last_verified ? sheet.last_verified.slice(0, 10) : ""}
              onChange={(e) => setField("last_verified", e.target.value || null)}
              data-testid="sheet-field-last_verified"
            />
          </div>
          <div className="text-xs text-muted-foreground">
            Le PDF A4 n'est téléchargeable qu'une fois la fiche <strong>Validée</strong>, et uniquement par les adhérents actifs (contrôle serveur).
          </div>

          {/* Phase G2 suivi — Traductions + Dossier vivant */}
          <TranslationsPanel
            resource="practical-sheets"
            id={sheet.id}
            disabled={!(sheet.status === "ready" || sheet.status === "validated")}
          />
          <LivingDossierPanel sheetId={sheet.id} sheet={sheet} />
        </TabsContent>

        <TabsContent value="partners" className="mt-4">
          <PartnersPanel destinationId={sheet.id} />
        </TabsContent>
      </Tabs>

      {/* Media picker */}
      <Dialog open={openPicker} onOpenChange={setOpenPicker}>
        <DialogContent className="max-w-3xl" data-testid="media-picker-dialog">
          <DialogHeader>
            <DialogTitle>Médiathèque — photo principale + illustrations</DialogTitle>
            <DialogDescription>
              Cochez la couverture (étoile) et ajoutez les photos d'illustration. Ces médias sont
              alors comptés comme <strong>utilisés</strong> (jamais orphelins).
            </DialogDescription>
          </DialogHeader>
          <MediaGrid
            photos={photos}
            selectedCover={sheet.cover_media_id}
            selectedIds={selectedIds}
            onToggleCover={toggleCover}
            onToggle={togglePhoto}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenPicker(false)}>Fermer</Button>
            <Button onClick={() => { setOpenPicker(false); save(); }}>Enregistrer la sélection</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
